import React, { useState, useEffect } from 'react';
import {
  Wallet,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Store,
  Building,
  Play,
  Loader2,
  Copy,
  CheckCheck,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const DVAManagement = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('without'); // 'without' or 'with'

  // Stores without VT state
  const [storesWithoutDVA, setStoresWithoutDVA] = useState([]);
  const [loadingWithout, setLoadingWithout] = useState(true);

  // Stores with VT state
  const [storesWithDVA, setStoresWithDVA] = useState([]);
  const [loadingWith, setLoadingWith] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalWithDVA, setTotalWithDVA] = useState(0);

  // Shared state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [provisioningStoreId, setProvisioningStoreId] = useState(null);
  const [batchProvisioning, setBatchProvisioning] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    withDVA: 0,
    withoutDVA: 0,
    withVT: 0,
    withoutVT: 0,
    destinationFailed: 0
  });

  const loadStats = async () => {
    try {
      const [vtResponse, dvaResponse] = await Promise.all([
        fetch('/api/virtual-terminal/admin/stats', { credentials: 'include' }),
        fetch('/api/dva/admin/stats', { credentials: 'include' })
      ]);

      const vtData = await vtResponse.json();
      const dvaData = await dvaResponse.json();

      if (vtData.success || dvaData.success) {
        setStats({
          total: vtData.stats?.total || dvaData.stats?.total || 0,
          withVT: vtData.stats?.withVT || 0,
          withoutVT: vtData.stats?.withoutVT || 0,
          destinationFailed: vtData.stats?.destinationFailed || 0,
          withDVA: dvaData.stats?.withDVA || 0,
          withoutDVA: dvaData.stats?.withoutDVA || 0
        });
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadStoresWithoutDVA = async () => {
    try {
      setLoadingWithout(true);
      setError('');

      const response = await fetch('/api/virtual-terminal/admin/stores-without-vt', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setStoresWithoutDVA(data.stores || []);
      } else {
        setError(data.error || 'Failed to load stores');
      }
    } catch (err) {
      setError('Failed to load stores without Virtual Terminal');
      console.error(err);
    } finally {
      setLoadingWithout(false);
    }
  };

  const loadStoresWithDVA = async (page = 1, search = '') => {
    try {
      setLoadingWith(true);
      setError('');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        search
      });

      const response = await fetch(`/api/virtual-terminal/admin/stores-with-vt?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setStoresWithDVA(data.stores || []);
        setTotalPages(data.totalPages || 1);
        setTotalWithDVA(data.total || 0);
        setCurrentPage(page);
      } else {
        setError(data.error || 'Failed to load stores');
      }
    } catch (err) {
      setError('Failed to load stores with Virtual Terminal');
      console.error(err);
    } finally {
      setLoadingWith(false);
    }
  };

  const provisionSingleStore = async (storeId, storeName) => {
    try {
      setProvisioningStoreId(storeId);
      setError('');
      setSuccess('');

      const response = await fetch(`/api/virtual-terminal/admin/provision/${storeId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        const destinationNote = data.destinationWarning ? ` WhatsApp alerts need attention: ${data.destinationWarning}` : '';
        setSuccess(`Virtual Terminal provisioned for ${storeName}: ${data.virtualTerminal?.code}.${destinationNote}`);
        // Remove the store from the list
        setStoresWithoutDVA(prev => prev.filter(s => s.id !== storeId));
        // Refresh stats
        loadStats();
        // Refresh stores with VT if on that tab
        if (activeTab === 'with') {
          loadStoresWithDVA(currentPage, searchQuery);
        }
      } else {
        setError(data.error || 'Failed to provision Virtual Terminal');
      }
    } catch (err) {
      setError('Failed to provision Virtual Terminal for store');
      console.error(err);
    } finally {
      setProvisioningStoreId(null);
    }
  };

  const provisionAllStores = async () => {
    try {
      setBatchProvisioning(true);
      setError('');
      setSuccess('');

      const response = await fetch('/api/virtual-terminal/admin/provision-all', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(data.message || 'Virtual Terminal batch provisioning started. Check server logs for progress.');
        // Reload the list after a delay
        setTimeout(() => {
          loadStoresWithoutDVA();
          loadStats();
        }, 5000);
      } else {
        setError(data.error || 'Failed to start batch provisioning');
      }
    } catch (err) {
      setError('Failed to start batch provisioning');
      console.error(err);
    } finally {
      setBatchProvisioning(false);
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadStoresWithDVA(1, searchQuery);
  };

  const handleRefresh = () => {
    loadStats();
    if (activeTab === 'without') {
      loadStoresWithoutDVA();
    } else {
      loadStoresWithDVA(currentPage, searchQuery);
    }
  };

  useEffect(() => {
    loadStats();
    loadStoresWithoutDVA();
    loadStoresWithDVA(1, '');
  }, []);

  const loading = activeTab === 'without' ? loadingWithout : loadingWith;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
            Payment Rails
          </h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleRefresh}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            {activeTab === 'without' && (
              <button
                onClick={provisionAllStores}
                disabled={batchProvisioning || storesWithoutDVA.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: storesWithoutDVA.length === 0 ? '#d1d5db' : '#0d7463',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: storesWithoutDVA.length === 0 || batchProvisioning ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {batchProvisioning ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Provision VT ({storesWithoutDVA.length})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
          Manage Paystack Virtual Terminal rollout while keeping DVA visible as a legacy bank-transfer rail.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fee2e2',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#dc2626'
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#16a34a'
        }}>
          <CheckCircle2 size={18} />
          {success}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#f3f4f6', borderRadius: '10px' }}>
              <Store size={20} color="#6b7280" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Total Approved Stores</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
            {stats.total}
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#dcfce7', borderRadius: '10px' }}>
              <Wallet size={20} color="#16a34a" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Active VT</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#16a34a' }}>
            {stats.withVT}
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#fef3c7', borderRadius: '10px' }}>
              <AlertCircle size={20} color="#d97706" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Pending VT</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#d97706' }}>
            {stats.withoutVT}
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#eef2ff', borderRadius: '10px' }}>
              <Building size={20} color="#4f46e5" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Legacy DVA</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#4f46e5' }}>
            {stats.withDVA}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        background: '#f3f4f6',
        padding: '4px',
        borderRadius: '10px',
        marginBottom: '20px',
        width: 'fit-content'
      }}>
        <button
          onClick={() => setActiveTab('without')}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === 'without' ? 'white' : 'transparent',
            color: activeTab === 'without' ? '#0d7463' : '#6b7280',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: activeTab === 'without' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          Pending VT ({stats.withoutVT})
        </button>
        <button
          onClick={() => setActiveTab('with')}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === 'with' ? 'white' : 'transparent',
            color: activeTab === 'with' ? '#0d7463' : '#6b7280',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: activeTab === 'with' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          Active VT ({stats.withVT})
        </button>
      </div>

      {/* Search (only for Active VT tab) */}
      {activeTab === 'with' && (
        <form onSubmit={handleSearch} style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '8px', maxWidth: '400px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, slug, terminal code..."
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                background: '#0d7463',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Search
            </button>
          </div>
        </form>
      )}

      {/* Stores List */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
            {activeTab === 'without' ? 'Stores Without Virtual Terminal' : 'Stores With Virtual Terminal'}
          </h2>
          <span style={{
            padding: '4px 12px',
            background: activeTab === 'without'
              ? (storesWithoutDVA.length > 0 ? '#fef3c7' : '#dcfce7')
              : '#dbeafe',
            color: activeTab === 'without'
              ? (storesWithoutDVA.length > 0 ? '#92400e' : '#166534')
              : '#1e40af',
            borderRadius: '9999px',
            fontSize: '13px',
            fontWeight: '500'
          }}>
            {activeTab === 'without' ? storesWithoutDVA.length : totalWithDVA} stores
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Loader2 size={32} className="animate-spin" style={{ color: '#6b7280', margin: '0 auto 16px' }} />
            <p style={{ color: '#6b7280' }}>Loading stores...</p>
          </div>
        ) : activeTab === 'without' ? (
          /* Stores WITHOUT VT */
          storesWithoutDVA.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#dcfce7',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <CheckCircle2 size={32} color="#16a34a" />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                All Caught Up!
              </h3>
              <p style={{ color: '#6b7280' }}>
                All approved stores have their virtual terminals provisioned.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Store</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Slug</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Eligibility</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Owner Email</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Created</th>
                    <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {storesWithoutDVA.map((store, index) => (
                    <tr
                      key={store.id}
                      style={{
                        borderBottom: index < storesWithoutDVA.length - 1 ? '1px solid #e5e7eb' : 'none'
                      }}
                    >
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            background: '#f3f4f6',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Store size={18} color="#6b7280" />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>{store.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {store.id.slice(0, 8)}...</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <code style={{
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: '#374151'
                        }}>
                          {store.slug}
                        </code>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
                          <span style={{ color: store.applicationStatus === 'APPROVED' ? '#166534' : '#92400e', fontWeight: 600 }}>
                            Store: {store.applicationStatus || 'PENDING'}
                          </span>
                          <span style={{ color: store.owner?.merchantKYC?.status === 'APPROVED' ? '#166534' : '#6b7280' }}>
                            KYC: {store.owner?.merchantKYC?.status || 'NOT_STARTED'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: '14px' }}>
                        {store.owner?.email || 'N/A'}
                      </td>
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: '14px' }}>
                        {new Date(store.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                        <button
                          onClick={() => provisionSingleStore(store.id, store.name)}
                          disabled={provisioningStoreId === store.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            background: provisioningStoreId === store.id ? '#d1d5db' : '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: provisioningStoreId === store.id ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          {provisioningStoreId === store.id ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Provisioning...
                            </>
                          ) : (
                            <>
                              <Wallet size={14} />
                              Provision VT
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          /* Stores WITH VT */
          storesWithDVA.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#f3f4f6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Wallet size={32} color="#6b7280" />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                No Results
              </h3>
              <p style={{ color: '#6b7280' }}>
                {searchQuery ? 'No stores match your search.' : 'No stores have Virtual Terminal provisioned yet.'}
              </p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Store</th>
                      <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Terminal Code</th>
                      <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                      <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>WhatsApp Alerts</th>
                      <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>VT Bank Details</th>
                      <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Legacy DVA</th>
                      <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Provisioned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storesWithDVA.map((store, index) => (
                      <tr
                        key={store.id}
                        style={{
                          borderBottom: index < storesWithDVA.length - 1 ? '1px solid #e5e7eb' : 'none'
                        }}
                      >
                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              background: '#dcfce7',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Store size={18} color="#16a34a" />
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: '#111827' }}>{store.name}</div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>{store.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <code style={{
                              padding: '6px 10px',
                              background: '#f0fdf4',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#166534',
                              letterSpacing: '0.5px'
                            }}>
	                              {store.paystackVirtualTerminalCode}
	                            </code>
	                            <button
	                              onClick={() => copyToClipboard(store.paystackVirtualTerminalCode, `vt-${store.id}`)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                borderRadius: '4px'
                              }}
	                              title="Copy terminal code"
	                            >
	                              {copiedId === `vt-${store.id}` ? (
	                                <CheckCheck size={16} color="#16a34a" />
	                              ) : (
                                <Copy size={16} color="#6b7280" />
                              )}
                            </button>
                          </div>
                        </td>
	                        <td style={{ padding: '16px 20px', color: '#374151', fontSize: '14px' }}>
	                          <span style={{
	                            padding: '4px 10px',
	                            background: store.virtualTerminalStatus === 'ACTIVE' ? '#dcfce7' : '#fef3c7',
	                            color: store.virtualTerminalStatus === 'ACTIVE' ? '#166534' : '#92400e',
	                            borderRadius: '9999px',
	                            fontSize: '12px',
	                            fontWeight: '600'
	                          }}>
	                            {store.virtualTerminalStatus || 'UNKNOWN'}
	                          </span>
	                        </td>
	                        <td style={{ padding: '16px 20px' }}>
	                          <div style={{ color: '#374151', fontSize: '14px' }}>
	                            <div>{store.virtualTerminalDestinationPhone || 'Not configured'}</div>
	                            <div style={{
	                              color: store.virtualTerminalDestinationStatus === 'FAILED' ? '#dc2626' : '#6b7280',
	                              fontSize: '12px',
	                              marginTop: '2px'
	                            }}>
	                              {store.virtualTerminalDestinationStatus || 'NOT_CONFIGURED'}
	                            </div>
	                          </div>
	                        </td>
	                        <td style={{ padding: '16px 20px' }}>
	                          <div style={{ color: '#374151', fontSize: '14px' }}>
	                            {store.virtualTerminalAccountNumber ? (
	                              <>
	                                <div>{store.virtualTerminalBank || 'Bank'} • {store.virtualTerminalAccountNumber}</div>
	                                <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
	                                  {store.virtualTerminalAccountName || store.name}
	                                </div>
	                              </>
	                            ) : (
	                              <span style={{ color: '#9ca3af' }}>Awaiting Paystack details</span>
	                            )}
	                          </div>
	                        </td>
	                        <td style={{ padding: '16px 20px' }}>
	                          <div style={{ color: '#374151', fontSize: '14px' }}>
	                            {store.virtualAccountNumber ? (
	                              <>
	                                <div>{store.virtualAccountBank || 'Bank'} • {store.virtualAccountNumber}</div>
	                                <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
	                                  Legacy active
	                                </div>
	                              </>
	                            ) : (
	                              <span style={{ color: '#9ca3af' }}>None</span>
	                            )}
	                          </div>
	                        </td>
	                        <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: '14px' }}>
	                          {store.virtualTerminalCreatedAt ? new Date(store.virtualTerminalCreatedAt).toLocaleDateString() : 'N/A'}
	                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  padding: '16px 20px',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => loadStoresWithDVA(currentPage - 1, searchQuery)}
                      disabled={currentPage === 1}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '8px 12px',
                        background: currentPage === 1 ? '#f3f4f6' : 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        color: currentPage === 1 ? '#9ca3af' : '#374151',
                        fontSize: '14px'
                      }}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>
                    <button
                      onClick={() => loadStoresWithDVA(currentPage + 1, searchQuery)}
                      disabled={currentPage === totalPages}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '8px 12px',
                        background: currentPage === totalPages ? '#f3f4f6' : 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        color: currentPage === totalPages ? '#9ca3af' : '#374151',
                        fontSize: '14px'
                      }}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )
        )}
      </div>

      {/* Info Card */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '12px'
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#0369a1', marginBottom: '8px' }}>
          About Payment Rails
        </h3>
        <ul style={{ margin: 0, padding: '0 0 0 20px', color: '#0369a1', fontSize: '13px', lineHeight: '1.8' }}>
          <li>Virtual Terminal is the default Paystack rail for newly approved merchants</li>
          <li>DVA remains visible as a legacy/manual bank-transfer rail during migration</li>
          <li>Virtual Terminal uses the same KYC/store phone source DVA already used</li>
          <li>WhatsApp alert setup is tracked separately from terminal provisioning</li>
          <li>Batch provisioning queues pending VT stores with rate limiting</li>
        </ul>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

export default DVAManagement;
